import { useEffect, useState, useCallback } from "react"
import { Link } from "react-router-dom"
import {
  IconClipboardCheck,
  IconLoader2,
  IconCheck,
  IconX,
  IconArrowsExchange,
} from "@tabler/icons-react"
import { useAppSelector } from "@/store"
import { hasPermission } from "@/store/permissionsSlice"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { api } from "@/lib/api"
import { toast } from "sonner"

// ── Types ────────────────────────────────────────────────────────────

interface PersonChangeValues {
  firstName: string
  lastName: string
  middleName: string | null
  socialSecurityNumber: string | null
  birthDate: string
  gender: number
}

interface PersonChangePayload {
  before: PersonChangeValues
  after: PersonChangeValues
}

interface PersonChangeRequestEventDto {
  id: number
  eventType: string
  actorId: string
  actorName: string
  notes: string | null
  createdAt: string
}

interface PersonChangeRequestDto {
  id: number
  personId: string
  personName: string
  changes: PersonChangePayload
  status: string
  createdBy: string
  createdByName: string
  assignedTo: string
  assignedToName: string
  createdAt: string
  events: PersonChangeRequestEventDto[]
}

interface ReviewerDto {
  userId: string
  displayName: string
}

interface UserDto {
  id: string
  displayName: string
  userPrincipalName: string
}

const genderLabels: Record<number, string> = {
  0: "Female",
  1: "Male",
  2: "Trans-Male",
  3: "Trans-Female",
  4: "Gender Non-Conforming",
  5: "Unknown",
}

// ── Diff Helper ──────────────────────────────────────────────────────

function ChangeDiff({ before, after }: { before: PersonChangeValues; after: PersonChangeValues }) {
  const fields: { label: string; before: string; after: string }[] = []

  if (before.firstName !== after.firstName) {
    fields.push({ label: "First Name", before: before.firstName, after: after.firstName })
  }
  if (before.lastName !== after.lastName) {
    fields.push({ label: "Last Name", before: before.lastName, after: after.lastName })
  }
  if ((before.middleName ?? "") !== (after.middleName ?? "")) {
    fields.push({ label: "Middle Name", before: before.middleName ?? "—", after: after.middleName ?? "—" })
  }
  if ((before.socialSecurityNumber ?? "") !== (after.socialSecurityNumber ?? "")) {
    fields.push({
      label: "SSN",
      before: before.socialSecurityNumber ?? "—",
      after: after.socialSecurityNumber ?? "—",
    })
  }
  if (before.birthDate !== after.birthDate) {
    fields.push({ label: "Date of Birth", before: before.birthDate, after: after.birthDate })
  }
  if (before.gender !== after.gender) {
    fields.push({
      label: "Gender",
      before: genderLabels[before.gender] ?? String(before.gender),
      after: genderLabels[after.gender] ?? String(after.gender),
    })
  }

  if (fields.length === 0) {
    return <p className="text-muted-foreground text-sm">No changes detected.</p>
  }

  return (
    <div className="space-y-2">
      {fields.map((f) => (
        <div key={f.label} className="text-sm">
          <span className="font-medium">{f.label}:</span>{" "}
          <span className="text-red-600 line-through">{f.before}</span>{" "}
          <span className="text-green-600">→ {f.after}</span>
        </div>
      ))}
    </div>
  )
}

// ── Component ────────────────────────────────────────────────────────

export default function ChangeRequestsPage() {
  const { permissions } = useAppSelector((state) => state.permissions)
  const [requests, setRequests] = useState<PersonChangeRequestDto[]>([])
  const [loading, setLoading] = useState(true)
  const [reviewers, setReviewers] = useState<ReviewerDto[]>([])
  const [actionLoading, setActionLoading] = useState<number | null>(null)

  // Staff selection state
  const [users, setUsers] = useState<UserDto[]>([])
  const [currentUser, setCurrentUser] = useState<UserDto | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string>("")
  const [usersLoading, setUsersLoading] = useState(true)

  // Review dialog state
  const [reviewDialog, setReviewDialog] = useState<{
    open: boolean
    action: "approve" | "reject"
    requestId: number
  } | null>(null)
  const [reviewNotes, setReviewNotes] = useState("")

  // Reassign dialog state
  const [reassignDialog, setReassignDialog] = useState<{
    open: boolean
    requestId: number
  } | null>(null)
  const [reassignTo, setReassignTo] = useState("")

  const loadRequests = useCallback(async () => {
    if (!selectedUserId) return
    setLoading(true)
    try {
      const data = await api.get<PersonChangeRequestDto[]>(
        `/api/people/change-requests?status=pending&userId=${encodeURIComponent(selectedUserId)}`
      )
      setRequests(data)
    } catch (err) {
      console.error("Failed to load change requests:", err)
      toast.error("Failed to load change requests.")
    } finally {
      setLoading(false)
    }
  }, [selectedUserId])

  const loadReviewers = useCallback(async () => {
    try {
      const data = await api.get<ReviewerDto[]>(
        "/api/people/change-requests/reviewers"
      )
      setReviewers(data)
    } catch {
      // Not critical
    }
  }, [])

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const [allUsers, me] = await Promise.all([
          api.get<UserDto[]>("/api/users"),
          api.get<UserDto>("/api/users/me"),
        ])
        setUsers(allUsers)
        setCurrentUser(me)
        setSelectedUserId(me.id)
      } catch (err) {
        console.error("Failed to load users", err)
      } finally {
        setUsersLoading(false)
      }
    }
    loadUsers()
  }, [])

  useEffect(() => {
    loadRequests()
    loadReviewers()
  }, [loadRequests, loadReviewers])

  const handleReview = async () => {
    if (!reviewDialog) return
    const { action, requestId } = reviewDialog
    setActionLoading(requestId)
    try {
      await api.post(`/api/people/change-requests/${requestId}:${action}`, {
        notes: reviewNotes || null,
      })
      toast.success(
        action === "approve"
          ? "Change request approved."
          : "Change request rejected. Person record has been reverted."
      )
      setReviewDialog(null)
      setReviewNotes("")
      loadRequests()
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to ${action}.`
      toast.error(message)
    } finally {
      setActionLoading(null)
    }
  }

  const handleReassign = async () => {
    if (!reassignDialog || !reassignTo) return
    setActionLoading(reassignDialog.requestId)
    try {
      await api.post(
        `/api/people/change-requests/${reassignDialog.requestId}:reassign`,
        { assignToUserId: reassignTo }
      )
      toast.success("Change request reassigned.")
      setReassignDialog(null)
      setReassignTo("")
      loadRequests()
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to reassign."
      toast.error(message)
    } finally {
      setActionLoading(null)
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const canReview = reviewers.some((r) => r.userId === currentUser?.id)
    || hasPermission(permissions, "people:review-changes")

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <IconClipboardCheck className="size-6" />
          <h1 className="text-2xl font-semibold tracking-tight">Change Requests</h1>
          <Badge variant="secondary" className="ml-2">
            {requests.length} pending
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {usersLoading ? (
            <Skeleton className="h-9 w-[200px]" />
          ) : (
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select staff..." />
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.displayName}
                    {u.id === currentUser?.id ? " (me)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {requests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <IconClipboardCheck className="size-12 mx-auto mb-4 opacity-30" />
            <p>
              No pending change requests assigned to {selectedUserId === currentUser?.id
                ? "you"
                : users.find((u) => u.id === selectedUserId)?.displayName ?? "this user"}.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((cr) => (
            <Card key={cr.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      <Link
                        to={`/people/${cr.personId}`}
                        className="text-primary hover:underline"
                      >
                        {cr.personName}
                      </Link>
                    </CardTitle>
                    <CardDescription>
                      Requested by {cr.createdByName} on{" "}
                      {formatDate(cr.createdAt)}
                    </CardDescription>
                  </div>
                  <Badge variant="outline">Pending</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ChangeDiff
                  before={cr.changes.before}
                  after={cr.changes.after}
                />
                {cr.events.length > 0 && (
                  <div className="border-t pt-3">
                    <p className="text-xs font-medium text-muted-foreground mb-2">History</p>
                    <div className="space-y-1">
                      {cr.events.map((e) => (
                        <div key={e.id} className="text-xs text-muted-foreground">
                          <span className="font-medium">{e.eventType}</span>
                          {" by "}{e.actorName}
                          {e.notes && <> — <span className="italic">{e.notes}</span></>}
                          {" · "}{formatDate(e.createdAt)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {canReview && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      setReviewDialog({
                        open: true,
                        action: "approve",
                        requestId: cr.id,
                      })
                    }
                    disabled={actionLoading === cr.id}
                  >
                    <IconCheck className="size-4 mr-1" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() =>
                      setReviewDialog({
                        open: true,
                        action: "reject",
                        requestId: cr.id,
                      })
                    }
                    disabled={actionLoading === cr.id}
                  >
                    <IconX className="size-4 mr-1" />
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setReassignDialog({ open: true, requestId: cr.id })
                    }
                    disabled={actionLoading === cr.id}
                  >
                    <IconArrowsExchange className="size-4 mr-1" />
                    Reassign
                  </Button>
                  {actionLoading === cr.id && (
                    <IconLoader2 className="size-4 animate-spin self-center" />
                  )}
                </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Review Dialog */}
      <Dialog
        open={reviewDialog?.open ?? false}
        onOpenChange={(open) => {
          if (!open) {
            setReviewDialog(null)
            setReviewNotes("")
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewDialog?.action === "approve"
                ? "Approve Change Request"
                : "Reject Change Request"}
            </DialogTitle>
            <DialogDescription>
              {reviewDialog?.action === "approve"
                ? "The changes will remain on the person record."
                : "The person record will be reverted to its original values."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Add notes about your decision..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setReviewDialog(null)
                setReviewNotes("")
              }}
            >
              Cancel
            </Button>
            <Button
              variant={
                reviewDialog?.action === "reject" ? "destructive" : "default"
              }
              onClick={handleReview}
              disabled={actionLoading !== null}
            >
              {actionLoading && (
                <IconLoader2 className="size-4 mr-2 animate-spin" />
              )}
              {reviewDialog?.action === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog
        open={reassignDialog?.open ?? false}
        onOpenChange={(open) => {
          if (!open) {
            setReassignDialog(null)
            setReassignTo("")
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Change Request</DialogTitle>
            <DialogDescription>
              Select a reviewer to reassign this change request to.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reassignTo">Assign to</Label>
            <Select value={reassignTo} onValueChange={setReassignTo}>
              <SelectTrigger id="reassignTo">
                <SelectValue placeholder="Select reviewer" />
              </SelectTrigger>
              <SelectContent>
                {reviewers.map((r) => (
                  <SelectItem key={r.userId} value={r.userId}>
                    {r.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setReassignDialog(null)
                setReassignTo("")
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReassign}
              disabled={!reassignTo || actionLoading !== null}
            >
              {actionLoading && (
                <IconLoader2 className="size-4 mr-2 animate-spin" />
              )}
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
